import type { ReactNode } from 'react';
import { ChevronLeft } from 'lucide-react';
import { IconButton, OnboardingProgressBar } from '@/components/ui';

export interface OnboardingLayoutProps {
  onBack?: () => void;
  showBackButton?: boolean;
  showProgressBar?: boolean;
  progress?: number;
  /**
   * Optional action rendered on the right side of the sticky header —
   * e.g. a "?" button that opens troubleshooting on the forwarding
   * setup step. Kept as a freeform ReactNode so callers can drop in an
   * <IconButton>/<Button> without the layout needing to know what they
   * mean.
   */
  headerRight?: ReactNode;
  stickyFooter?: ReactNode;
  /**
   * False lets the footer scroll with the content instead of pinning — for an
   * overlay host whose on-screen keyboard is open, where a pinned CTA would
   * eat the space left above the keyboard (YAR-508). Defaults to true.
   */
  pinFooter?: boolean;
  children: ReactNode;
}

export function OnboardingLayout({
  onBack,
  showBackButton,
  showProgressBar,
  progress,
  headerRight,
  stickyFooter,
  pinFooter = true,
  children,
}: OnboardingLayoutProps) {
  return (
    // h-dvh (not min-h-dvh): Figma frames the whole flow inside one mobile
    // viewport — chrome (header/progress/footer) stays fixed and long
    // content scrolls INSIDE <main>, never the page.
    <div className="h-dvh flex flex-col bg-surface">
      {/* Sticky header */}
      <header className="sticky top-0 z-[100] bg-background px-5 py-2 flex items-center justify-between">
        <div className="flex items-center">
          {showBackButton && (
            <IconButton
              onClick={onBack}
              aria-label="Go back"
              variant="ghost"
            >
              <ChevronLeft className="w-5 h-5 text-icon-default" />
            </IconButton>
          )}
        </div>
        {headerRight && <div className="flex items-center">{headerRight}</div>}
      </header>

      {/* Progress bar */}
      {showProgressBar && <OnboardingProgressBar progress={progress ?? 0} />}

      {/* Scrollable content — page margins 20px, 40px vertical (Figma
          content py-40). The footer sits in normal flow below <main>
          (h-dvh shell) so content never slides under it; pb-10 is
          breathing room above the pinned footer, not overlay clearance
          (the old pb-24 starved height-bounded steps on short phones). */}
      <main className="flex-1 flex flex-col overflow-y-auto px-5 pt-10 pb-10">
        {children}
      </main>

      {/* Sticky footer for CTA */}
      {stickyFooter && (
        // 40px below the CTA = Figma `spacing/component/4xl`. It was 20px
        // (`spacing/component/xl`), which QA reported as "less space under the
        // Continue button than Figma" on YAR-158, YAR-159, YAR-160 and
        // YAR-284. max() keeps the home-indicator inset on iOS, where the
        // safe area can exceed 40px.
        <footer
          className={[
            'z-[100] bg-background px-5 pb-[max(2.5rem,env(safe-area-inset-bottom))] pt-3',
            pinFooter ? 'sticky bottom-0' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {stickyFooter}
        </footer>
      )}
    </div>
  );
}
